import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  adminCreatePricing,
  adminDeletePricing,
  adminListPricing,
  adminUpdatePricing,
} from "../lib/api";

const EMPTY_NEW = {
  id: "",
  name: "",
  unit: "per piece",
  price: 0,
  original_price: null,
  description: "",
  turnaround: "48 hours",
  order: 99,
  active: true,
};

export default function PricingTab({ adminKey }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [drafts, setDrafts] = useState({}); // service_id -> patch
  const [newForm, setNewForm] = useState(EMPTY_NEW);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminListPricing(adminKey);
      setServices(r.services || []);
      setDrafts({});
    } catch (e) {
      toast.error("Could not load pricing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (id, field, value) => {
    setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), [field]: value } }));
  };

  const saveRow = async (svc) => {
    const patch = drafts[svc.id];
    if (!patch) return;
    setSavingId(svc.id);
    try {
      if (patch.price !== undefined) patch.price = Number(patch.price);
      if (patch.original_price !== undefined && patch.original_price !== null) patch.original_price = Number(patch.original_price);
      if (patch.order !== undefined) patch.order = parseInt(patch.order, 10) || 0;
      await adminUpdatePricing(adminKey, svc.id, patch);
      toast.success(`${svc.name} updated`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSavingId(null);
    }
  };

  const toggleActive = async (svc) => {
    try {
      await adminUpdatePricing(adminKey, svc.id, { active: !svc.active });
      toast.success(`${svc.name} ${!svc.active ? "enabled" : "hidden"}`);
      load();
    } catch (e) {
      toast.error("Toggle failed");
    }
  };

  const removeService = async (svc) => {
    if (!window.confirm(`Delete "${svc.name}"? This removes it from the customer menu.`)) return;
    try {
      await adminDeletePricing(adminKey, svc.id);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const createNew = async (e) => {
    e.preventDefault();
    if (!newForm.id.trim() || !newForm.name.trim()) {
      toast.error("id and name are required");
      return;
    }
    setCreating(true);
    try {
      await adminCreatePricing(adminKey, {
        ...newForm,
        id: newForm.id.trim().toLowerCase().replace(/\s+/g, "_"),
        price: Number(newForm.price) || 0,
        order: parseInt(newForm.order, 10) || 0,
      });
      toast.success("Service added");
      setNewForm(EMPTY_NEW);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const dirtyCount = Object.keys(drafts).length;

  const saveAll = async () => {
    setSavingId("__all__");
    try {
      for (const svc of services) {
        const patch = drafts[svc.id];
        if (!patch) continue;
        if (patch.price !== undefined) patch.price = Number(patch.price);
        if (patch.original_price !== undefined && patch.original_price !== null) patch.original_price = Number(patch.original_price);
        if (patch.order !== undefined) patch.order = parseInt(patch.order, 10) || 0;
        await adminUpdatePricing(adminKey, svc.id, patch);
      }
      toast.success(`Saved ${dirtyCount} service${dirtyCount > 1 ? "s" : ""} — live now`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingId(null);
    }
  };

  const value = (svc, field) => {
    const d = drafts[svc.id];
    if (d && d[field] !== undefined) return d[field];
    return svc[field];
  };

  return (
    <div className="mt-6 space-y-6" data-testid="admin-pricing-panel">
      <div className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
          <div>
            <div className="font-display text-xl">Live pricing</div>
            <p className="text-xs text-rw-ink3 mt-1">
              Edit a row, then hit <span className="font-semibold">Save</span>. Changes take effect
              instantly on the customer site (Home, Booking, /api/pricing).
            </p>
          </div>
          <button
            onClick={load}
            className="rounded-full border border-rw-ink/20 px-4 py-2 text-sm hover:bg-rw-bg3"
            data-testid="pricing-refresh-btn"
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="py-10 text-center text-rw-ink2">Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-rw-bg3 text-left text-rw-ink2">
                <tr>
                  <th className="px-3 py-3 font-semibold border-b border-rw-ink/10">ID</th>
                  <th className="px-3 py-3 font-semibold border-b border-rw-ink/10">Name</th>
                  <th className="px-3 py-3 font-semibold border-b border-rw-ink/10">Unit</th>
                  <th className="px-3 py-3 font-semibold border-b border-rw-ink/10">Price (₹)</th>
                  <th className="px-3 py-3 font-semibold border-b border-rw-ink/10">Was (₹)</th>
                  <th className="px-3 py-3 font-semibold border-b border-rw-ink/10">Turnaround</th>
                  <th className="px-3 py-3 font-semibold border-b border-rw-ink/10">Description</th>
                  <th className="px-3 py-3 font-semibold border-b border-rw-ink/10">Order</th>
                  <th className="px-3 py-3 font-semibold border-b border-rw-ink/10">Live</th>
                  <th className="px-3 py-3 font-semibold border-b border-rw-ink/10 sticky right-0 bg-rw-bg3 shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.15)]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {services.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-4 py-10 text-center text-rw-ink2">
                      No services yet. Add your first one below.
                    </td>
                  </tr>
                )}
                {services.map((svc) => {
                  const dirty = !!drafts[svc.id];
                  return (
                    <tr key={svc.id} className="border-b border-rw-ink/10" data-testid={`pricing-row-${svc.id}`}>
                      <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{svc.id}</td>
                      <td className="px-3 py-2">
                        <input
                          className="w-40 rounded-md border border-rw-ink/20 bg-white px-2 py-1 text-sm"
                          value={value(svc, "name") || ""}
                          onChange={(e) => setField(svc.id, "name", e.target.value)}
                          data-testid={`pricing-name-${svc.id}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          className="w-24 rounded-md border border-rw-ink/20 bg-white px-2 py-1 text-sm"
                          value={value(svc, "unit") || ""}
                          onChange={(e) => setField(svc.id, "unit", e.target.value)}
                          data-testid={`pricing-unit-${svc.id}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min="0"
                          step="1"
                          className="w-24 rounded-md border border-rw-ink/20 bg-white px-2 py-1 text-sm font-display"
                          value={value(svc, "price") ?? 0}
                          onChange={(e) => setField(svc.id, "price", e.target.value)}
                          data-testid={`pricing-price-${svc.id}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min="0"
                          step="1"
                          className="w-24 rounded-md border border-rw-ink/20 bg-white px-2 py-1 text-sm text-rw-ink3"
                          value={value(svc, "original_price") ?? ""}
                          onChange={(e) => setField(svc.id, "original_price", e.target.value === "" ? null : e.target.value)}
                          placeholder="—"
                          data-testid={`pricing-original-${svc.id}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          className="w-28 rounded-md border border-rw-ink/20 bg-white px-2 py-1 text-sm"
                          value={value(svc, "turnaround") || ""}
                          onChange={(e) => setField(svc.id, "turnaround", e.target.value)}
                          data-testid={`pricing-turnaround-${svc.id}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          className="w-64 rounded-md border border-rw-ink/20 bg-white px-2 py-1 text-sm"
                          value={value(svc, "description") || ""}
                          onChange={(e) => setField(svc.id, "description", e.target.value)}
                          data-testid={`pricing-desc-${svc.id}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          className="w-16 rounded-md border border-rw-ink/20 bg-white px-2 py-1 text-sm"
                          value={value(svc, "order") ?? 0}
                          onChange={(e) => setField(svc.id, "order", e.target.value)}
                          data-testid={`pricing-order-${svc.id}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => toggleActive(svc)}
                          className={`text-xs rounded-full px-3 py-1 font-semibold ${
                            svc.active ? "bg-rw-mint" : "border border-rw-ink/20 text-rw-ink3"
                          }`}
                          data-testid={`pricing-active-${svc.id}`}
                        >
                          {svc.active ? "Live" : "Hidden"}
                        </button>
                      </td>
                      <td className="px-3 py-2 sticky right-0 bg-rw-bg2 shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.15)]">
                        <div className="flex gap-2">
                          <button
                            onClick={() => saveRow(svc)}
                            disabled={!dirty || savingId === svc.id}
                            className={`text-xs rounded-full px-3 py-1.5 font-semibold ${
                              dirty ? "bg-rw-ink text-white" : "border border-rw-ink/20 text-rw-ink3"
                            } disabled:opacity-50`}
                            data-testid={`pricing-save-${svc.id}`}
                          >
                            {savingId === svc.id ? "Saving…" : "Save"}
                          </button>
                          <button
                            onClick={() => removeService(svc)}
                            className="text-xs rounded-full bg-rw-peach/60 px-3 py-1.5 font-semibold"
                            data-testid={`pricing-delete-${svc.id}`}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {dirtyCount > 0 && (
        <div
          className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 rounded-full bg-rw-ink text-white pl-5 pr-2 py-2 shadow-2xl"
          data-testid="pricing-save-bar"
        >
          <span className="text-sm">{dirtyCount} unsaved change{dirtyCount > 1 ? "s" : ""}</span>
          <button
            onClick={() => setDrafts({})}
            className="text-xs text-white/70 hover:text-white"
            data-testid="pricing-discard-all-btn"
          >
            Discard
          </button>
          <button
            onClick={saveAll}
            disabled={savingId === "__all__"}
            className="rounded-full bg-white text-rw-ink px-5 py-2 text-sm font-semibold hover:bg-rw-mint transition-colors disabled:opacity-50"
            data-testid="pricing-save-all-btn"
          >
            {savingId === "__all__" ? "Saving…" : "Save all changes"}
          </button>
        </div>
      )}

      {/* Add new */}
      <form
        onSubmit={createNew}
        className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-5 space-y-3"
        data-testid="pricing-new-form"
      >
        <div className="font-display text-xl">Add a new service</div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <label className="block">
            <span className="overline">ID</span>
            <input
              className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm font-mono"
              value={newForm.id}
              onChange={(e) => setNewForm({ ...newForm, id: e.target.value })}
              placeholder="e.g. shoe_clean"
              data-testid="pricing-new-id"
            />
          </label>
          <label className="block">
            <span className="overline">Name</span>
            <input
              className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm"
              value={newForm.name}
              onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
              placeholder="Shoe cleaning"
              data-testid="pricing-new-name"
            />
          </label>
          <label className="block">
            <span className="overline">Unit</span>
            <input
              className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm"
              value={newForm.unit}
              onChange={(e) => setNewForm({ ...newForm, unit: e.target.value })}
              data-testid="pricing-new-unit"
            />
          </label>
          <label className="block">
            <span className="overline">Price (₹)</span>
            <input
              type="number"
              min="0"
              className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm"
              value={newForm.price}
              onChange={(e) => setNewForm({ ...newForm, price: e.target.value })}
              data-testid="pricing-new-price"
            />
          </label>
          <label className="block">
            <span className="overline">Was price (₹, optional)</span>
            <input
              type="number"
              min="0"
              className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm"
              value={newForm.original_price ?? ""}
              onChange={(e) => setNewForm({ ...newForm, original_price: e.target.value === "" ? null : Number(e.target.value) })}
              placeholder="Struck-through MRP"
              data-testid="pricing-new-original"
            />
          </label>
          <label className="block">
            <span className="overline">Turnaround</span>
            <input
              className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm"
              value={newForm.turnaround}
              onChange={(e) => setNewForm({ ...newForm, turnaround: e.target.value })}
              data-testid="pricing-new-turnaround"
            />
          </label>
          <label className="block">
            <span className="overline">Order</span>
            <input
              type="number"
              className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm"
              value={newForm.order}
              onChange={(e) => setNewForm({ ...newForm, order: e.target.value })}
              data-testid="pricing-new-order"
            />
          </label>
        </div>
        <label className="block">
          <span className="overline">Description</span>
          <input
            className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm"
            value={newForm.description}
            onChange={(e) => setNewForm({ ...newForm, description: e.target.value })}
            placeholder="Short line customers see under the price"
            data-testid="pricing-new-desc"
          />
        </label>
        <button
          type="submit"
          disabled={creating}
          className="btn-dark rounded-full py-2.5 px-6 text-sm font-semibold disabled:opacity-40"
          data-testid="pricing-new-submit"
        >
          {creating ? "Adding…" : "Add service"}
        </button>
      </form>
    </div>
  );
}
